import { pushDataLayerEvent } from './dataLayer';
import { UserType } from './dataLayer.types';

export const SkyporterEvents = {
  signUpStarted() {
    pushDataLayerEvent({ event: 'sign_up_start' }, 'sign_up_start');
  },

  signUpCompleted(params: {
    userId: string | number;
    userType: UserType;
    method: 'email' | 'google' | 'phone';
    signupCountry?: string;
  }) {
    pushDataLayerEvent({
      event: 'sign_up_complete',
      user_type: params.userType,
      user_id: params.userId,
      method: params.method,
      signup_country: params.signupCountry,
    });
  },

  login(params: {
    userId: string | number;
    userType: UserType;
    method: 'email' | 'google' | 'phone';
  }) {
    pushDataLayerEvent({
      event: 'login',
      user_type: params.userType,
      user_id: params.userId,
      method: params.method,
    });
  },

  roleSelected(params: { userId: string | number; role: UserType }) {
    pushDataLayerEvent({
      event: params.role === UserType.Traveller ? 'become_traveller' : 'become_sender',
      user_id: params.userId,
    });
  },

  itineraryPublished(params: {
    userId: string | number;
    itineraryId: string | number;
    originCountry?: string;
    destinationCountry?: string;
    departureDate?: string;
    capacityKg?: number;
    listingSequence?: number;
  }) {
    pushDataLayerEvent({
      event: 'itinerary_published',
      user_type: UserType.Traveller,
      user_id: params.userId,
      itinerary_id: params.itineraryId,
      origin_country: params.originCountry,
      destination_country: params.destinationCountry,
      departure_date: params.departureDate,
      capacity_kg: params.capacityKg,
      listing_sequence: params.listingSequence,
    });
  },

  itemPublished(params: {
    userId: string | number;
    itemId: string | number;
    originCountry?: string;
    destinationCountry?: string;
    itemCategory?: string;
    weightKg?: number;
    listingSequence?: number;
  }) {
    pushDataLayerEvent({
      event: 'item_published',
      user_type: UserType.Sender,
      user_id: params.userId,
      item_id: params.itemId,
      origin_country: params.originCountry,
      destination_country: params.destinationCountry,
      item_category: params.itemCategory,
      weight_kg: params.weightKg,
      listing_sequence: params.listingSequence,
    });
  },

  dealInitiated(params: {
    userId: string | number;
    userType: UserType;
    initiatedBy: UserType;
    listingType: 'item' | 'itinerary';
    listingId: string | number;
    dealId: string | number;
    counterpartyId?: string | number;
    originCountry?: string;
    destinationCountry?: string;
  }) {
    pushDataLayerEvent(
      {
        event: 'deal_initiated',
        user_type: params.userType,
        user_id: params.userId,
        initiated_by: params.initiatedBy,
        listing_type: params.listingType,
        listing_id: params.listingId,
        deal_id: params.dealId,
        counterparty_id: params.counterpartyId,
        origin_country: params.originCountry,
        destination_country: params.destinationCountry,
      },
      `deal_initiated_${params.dealId}`,
    );
  },

  paymentCompleted(params: {
    userId: string | number;
    dealId: string | number;
    transactionId?: string;
    currency?: string;
    amount?: number;
    amountUsd?: number;
    travellerId?: string | number;
    flow: 'A' | 'B';
  }) {
    pushDataLayerEvent(
      {
        event: 'payment_complete',
        user_type: UserType.Sender,
        user_id: params.userId,
        deal_id: params.dealId,
        transaction_id: params.transactionId,
        currency: params.currency,
        amount: params.amount,
        amount_usd: params.amountUsd,
        traveller_id: params.travellerId,
        flow: params.flow,
      },
      `payment_complete_${params.dealId}`,
    );
  },

  dealConfirmed(params: {
    userId: string | number;
    userType: UserType;
    confirmedBy: 'sender_payment' | 'traveller';
    dealId: string | number;
    senderId?: string | number;
    travellerId?: string | number;
    originCountry?: string;
    destinationCountry?: string;
    amountUsd?: number;
    flow: 'A' | 'B';
  }) {
    pushDataLayerEvent(
      {
        event: 'deal_confirmed',
        user_type: params.userType,
        user_id: params.userId,
        confirmed_by: params.confirmedBy,
        deal_id: params.dealId,
        sender_id: params.senderId,
        traveller_id: params.travellerId,
        origin_country: params.originCountry,
        destination_country: params.destinationCountry,
        amount_usd: params.amountUsd,
        flow: params.flow,
      },
      `deal_confirmed_${params.dealId}`,
    );
  },

  itemTransitStarted(params: {
    userId: string | number;
    orderId: string | number;
  }) {
    pushDataLayerEvent({
      event: 'item_in_transit',
      user_type: UserType.Traveller,
      user_id: params.userId,
      order_id: params.orderId,
    });
  },

  profileComplete(params: { userId: string | number }) {
    pushDataLayerEvent({
      event: 'profile_complete',
      user_id: params.userId,
    });
  },

  kycVerificationStart(params: { userId: string | number }) {
    pushDataLayerEvent({
      event: 'kyc_verification_start',
      user_id: params.userId,
    });
  },

  kycVerificationCompleted(params: { userId: string | number }) {
    pushDataLayerEvent({
      event: 'kyc_verification_completed',
      user_id: params.userId,
    });
  },

  cardVerificationStart(params: { userId: string | number }) {
    pushDataLayerEvent({
      event: 'card_verification_start',
      user_id: params.userId,
    });
  },

  cardVerificationCompleted(params: { userId: string | number }) {
    pushDataLayerEvent({
      event: 'card_verification_completed',
      user_id: params.userId,
    });
  },

  itineraryCreationStarts(params: { userId: string | number }) {
    pushDataLayerEvent({
      event: 'itinerary_creation_starts',
      user_type: UserType.Traveller,
      user_id: params.userId,
    });
  },

  itineraryStepComplete(params: {
    userId: string | number;
    stepName: string;
    stepNumber: number;
  }) {
    pushDataLayerEvent({
      event: 'itinerary_step_complete',
      user_type: UserType.Traveller,
      user_id: params.userId,
      step_name: params.stepName,
      step_number: params.stepNumber,
    });
  },

  itemSearchPerformed(params: {
    userId: string | number;
    origin: string;
    destination: string;
    dateRange: string;
  }) {
    pushDataLayerEvent({
      event: 'item_search_performed',
      user_type: UserType.Traveller,
      user_id: params.userId,
      origin: params.origin,
      destination: params.destination,
      date_range: params.dateRange,
    });
  },

  itemViewed(params: { userId: string | number; itemId: string | number }) {
    pushDataLayerEvent({
      event: 'item_viewed',
      user_type: UserType.Traveller,
      user_id: params.userId,
      item_id: params.itemId,
    });
  },

  itemCreationStarts(params: { userId: string | number }) {
    pushDataLayerEvent({
      event: 'item_creation_starts',
      user_type: UserType.Sender,
      user_id: params.userId,
    });
  },

  itemStepComplete(params: {
    userId: string | number;
    stepName: string;
    stepNumber: number;
  }) {
    pushDataLayerEvent({
      event: 'item_step_complete',
      user_type: UserType.Sender,
      user_id: params.userId,
      step_name: params.stepName,
      step_number: params.stepNumber,
    });
  },

  itinerarySearchPerformed(params: {
    userId: string | number;
    origin: string;
    destination: string;
    dateRange: string;
  }) {
    pushDataLayerEvent({
      event: 'itinerary_search_performed',
      user_type: UserType.Sender,
      user_id: params.userId,
      origin: params.origin,
      destination: params.destination,
      date_range: params.dateRange,
    });
  },

  itineraryViewed(params: { userId: string | number; itineraryId: string | number }) {
    pushDataLayerEvent({
      event: 'itinerary_viewed',
      user_type: UserType.Sender,
      user_id: params.userId,
      itinerary_id: params.itineraryId,
    });
  },

  itemAccepted(params: { userId: string | number; dealId: string | number }) {
    pushDataLayerEvent(
      {
        event: 'item_accepted',
        user_type: UserType.Traveller,
        user_id: params.userId,
        deal_id: params.dealId,
      },
      `item_accepted_${params.dealId}`,
    );
  },

  itemCollected(params: { userId: string | number; orderId: string | number }) {
    pushDataLayerEvent({
      event: 'item_collected',
      user_type: UserType.Traveller,
      user_id: params.userId,
      order_id: params.orderId,
    });
  },

  itemDeliveryStarts(params: { userId: string | number; orderId?: string | number }) {
    pushDataLayerEvent({
      event: 'item_delivery_starts',
      user_type: UserType.Traveller,
      user_id: params.userId,
      order_id: params.orderId,
    });
  },

  itemDelivered(params: { userId: string | number; orderId: string | number }) {
    pushDataLayerEvent(
      {
        event: 'item_delivered',
        user_type: UserType.Traveller,
        user_id: params.userId,
        order_id: params.orderId,
      },
      `item_delivered_${params.orderId}`,
    );
  },

  senderReviewSubmitted(params: { userId: string | number; rating: number }) {
    pushDataLayerEvent({
      event: 'sender_review_submitted',
      user_type: UserType.Traveller,
      user_id: params.userId,
      rating: params.rating,
    });
  },

  itineraryAcceptedWoPayment(params: { userId: string | number; dealId: string | number }) {
    pushDataLayerEvent(
      {
        event: 'itinerary_accepted_wopayment',
        user_type: UserType.Sender,
        user_id: params.userId,
        deal_id: params.dealId,
      },
      `itinerary_accepted_wopayment_${params.dealId}`,
    );
  },

  proceedToPayment(params: { userId: string | number; dealId: string | number }) {
    pushDataLayerEvent(
      {
        event: 'proceed_to_payment',
        user_type: UserType.Sender,
        user_id: params.userId,
        deal_id: params.dealId,
      },
      `proceed_to_payment_${params.dealId}`,
    );
  },

  paymentInitiated(params: {
    userId: string | number;
    dealId: string | number;
    amount?: number;
    currency?: string;
  }) {
    pushDataLayerEvent(
      {
        event: 'payment_initiated',
        user_type: UserType.Sender,
        user_id: params.userId,
        deal_id: params.dealId,
        amount: params.amount,
        currency: params.currency,
      },
      `payment_initiated_${params.dealId}`,
    );
  },

  itemDropoffComplete(params: { userId: string | number; orderId: string | number }) {
    pushDataLayerEvent(
      {
        event: 'item_dropoff_complete',
        user_type: UserType.Sender,
        user_id: params.userId,
        order_id: params.orderId,
      },
      `item_dropoff_complete_${params.orderId}`,
    );
  },

  travellerReviewSubmitted(params: { userId: string | number; rating: number }) {
    pushDataLayerEvent({
      event: 'traveller_review_submitted',
      user_type: UserType.Sender,
      user_id: params.userId,
      rating: params.rating,
    });
  },

  acceptItemsTerms(params: { orderCode: string }) {
    pushDataLayerEvent({
      event: 'accept_items_terms',
      order_code: params.orderCode,
    });
  },

  receiverRequestsVerification(params: { orderCode: string }) {
    pushDataLayerEvent({
      event: 'receiver_requests_verification',
      order_code: params.orderCode,
    });
  },

  receiverVerificationComplete(params: { orderCode: string }) {
    pushDataLayerEvent({
      event: 'receiver_verification_complete',
      order_code: params.orderCode,
    });
  },

  deliveryAccepted(params: { orderCode: string }) {
    pushDataLayerEvent(
      {
        event: 'delivery_accepted',
        order_code: params.orderCode,
      },
      `delivery_accepted_${params.orderCode}`,
    );
  },
};
